import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { PeriodCoverageResponse } from '@interfaces/accounting-document-management/responses/document-coverage.response';
import {
  CELL_STATE_CLASS,
  CELL_STATE_LABEL,
  FILE_PROCESSING_STATUS_LABEL,
  monthLongLabel,
  resolveCellState,
} from '@features/dashboard/accounting-document-management/utils/coverage-view.util';

/** Uma linha de {@link PeriodCoverageResponse.files} — só o suficiente para a listagem/track. */
interface PeriodFileRow {
  extractionId: string;
}

/**
 * Painel do período selecionado (seção 6 do protótipo "Cobertura de documentos"): cabeçalho com o
 * selo do estado, aviso de duplicidade e lista de arquivos ("Considerado"/"Substituído" + `✕`). Só faz
 * sentido a página renderizar este componente quando há período selecionado — ele não sabe de
 * seleção, apenas do {@link period} que recebe.
 *
 * **Não tem edição manual de status/notas.** Ela existiu (decisão 9 do handoff PBI #2728) e foi
 * removida em 2026-08-12: a análise do documento é feita onde o documento é analisado — a tela de
 * documentos do onboarding, que grava `SubmittedDocument.StatusAnalysisDocuments` —, e manter um
 * segundo lugar para aprovar/rejeitar criava duas fontes para a mesma decisão. O status que este
 * painel exibe é derivado no backend.
 *
 * Componente de **apresentação pura**: só emite `fileOpened`/`fileRemoved` — não
 * injeta `ConfirmationService`, `DialogService` nem o state service da feature, e não chama HTTP.
 * Isso é intencional (unificação decidida na página orquestradora, PBI #2728): antes deste desenho,
 * este componente confirmava a remoção e abria a modal de preview por conta própria enquanto a
 * sanfona de exercícios (removida na unificação da tela em favor de `year-coverage`) fazia as duas
 * coisas via `output()` puro — duas experiências diferentes para a mesma ação (remover/abrir arquivo)
 * dependendo de onde o usuário clicava. A página é a única dona de ambos os fluxos
 * (`ConfirmationService.confirm()` antes de `stateService.deleteFile()`; `stateService.getDownloadLink()`
 * + `DialogService.open(FilePreviewModalComponent)`), aplicados igualmente a qualquer consumidor futuro
 * deste painel.
 *
 * **Inspeção inline (PBI #2728, Task 6; revisto em 21/08/2026)**: religado à tela unificada dentro do
 * `<ng-template>` (`selectionSlot`) que a página passa a `app-year-coverage` — nasce na trilha do
 * período selecionado, não mais no rodapé do card. Desde 21/08/2026 é o **único** conteúdo desse slot:
 * o `period-upload` que dividia o espaço com ele foi removido, e clicar numa célula passou a ser um
 * gesto de consulta (o que chegou naquele período, qual arquivo é o considerado), não de envio. Enviar
 * é só pela área de arraste do exercício, com classificação automática.
 */
@Component({
  selector: 'app-period-files-panel',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './period-files-panel.component.html',
  styleUrl: './period-files-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeriodFilesPanelComponent {
  readonly period = input.required<PeriodCoverageResponse>();

  /** `true` quando {@link period} é o slot anual de `EstruturaContabil` — muda só o rótulo do cabeçalho. */
  readonly isAnnual = input(false);

  /**
   * `true` quando o período é da trilha de Faturamento (card 3700). Ali não vale "o último enviado": vale a primeira
   * declaração validada em cada mês, e o aviso de duplicidade mostra a regra do backend ({@link ruleText}).
   */
  readonly isRevenue = input(false);

  /** Regra escrita do Faturamento (`ruleText` de `dfp-historical/revenue-statement/origin`); nula enquanto carrega. */
  readonly ruleText = input<string | null>(null);

  /** Emitido no clique do nome do arquivo — a página resolve o link e abre `FilePreviewModalComponent`. */
  readonly fileOpened = output<string>();

  /** Emitido no clique do `✕` — a página confirma via `ConfirmationService` antes de excluir. */
  readonly fileRemoved = output<string>();

  readonly cellStateLabel = CELL_STATE_LABEL;
  readonly fileStatusLabel = FILE_PROCESSING_STATUS_LABEL;

  readonly headerLabel = computed(() => {
    const period = this.period();
    return this.isAnnual()
      ? `Anual de ${period.year}`
      : `${monthLongLabel(period.month)} de ${period.year}`;
  });

  readonly cellState = computed(() => resolveCellState(this.period()));

  readonly cellStateClass = computed(() => CELL_STATE_CLASS[this.cellState()]);

  /** O arquivo "que conta" hoje para o período — usado no texto do aviso de duplicidade. */
  readonly consideredFile = computed(
    () => this.period().files.find(file => file.isConsidered) ?? null
  );

  readonly hasDuplicates = computed(() => this.period().files.length > 1);

  /**
   * Texto de "sem arquivo", que precisa distinguir dois casos completamente diferentes — dizer
   * "Nenhum arquivo neste período ainda" nos dois produzia uma contradição na cara do usuário:
   * o selo do período dizia **Enviado** e o corpo dizia que nada chegou.
   *
   * O que diferencia é o próprio estado do período: ele vem do inventário de demonstrativos extraídos, e
   * não dos arquivos. Então "Enviado" com zero arquivo significa que **os valores estão no sistema** e o
   * arquivo de origem não está mais vinculado aqui. Só quando o período está de fato pendente é que
   * "nada enviado ainda" é verdade.
   *
   * **Por que o texto afirma a causa (24/08/2026, Bug #2831).** Antes ele oferecia duas hipóteses —
   * "pode ter vindo de uma importação ou ter sido removido depois da extração" —, e a primeira não
   * acontece: em toda a base, **nenhuma** extração viva chega a este estado por importação. As 76 que
   * chegam têm submissão de onboarding **soft-deletada** (`SubmittedDocument.DeletedAt` preenchido, com
   * o `FilePath` e o ano ainda gravados na linha excluída), porque a remoção pela tela de onboarding
   * apaga só a submissão e deixa extração e valores extraídos vivos. Oferecer a hipótese errada junto da
   * certa era o que impedia o usuário de concluir o óbvio: o arquivo foi removido.
   */
  readonly emptyMessage = computed(() => {
    const state = this.cellState();
    const nadaChegou = state === 'pending' || state === 'notRequired';

    return nadaChegou
      ? 'Nenhum arquivo enviado para este período ainda.'
      : 'Os valores deste período já estão no sistema, mas o arquivo que os originou foi removido ' +
          'depois da extração e não está mais vinculado a este período.';
  });

  openFile(extractionId: string): void {
    this.fileOpened.emit(extractionId);
  }

  removeFile(extractionId: string): void {
    this.fileRemoved.emit(extractionId);
  }

  trackFile(_index: number, file: PeriodFileRow): string {
    return file.extractionId;
  }
}
